import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Save } from 'lucide-react';
import { useSchoolChapters } from '../../../pages/Exams/generate/hooks/useSchoolChapters';
import {
  usePaperDraft, usePaperDraftCreate, usePaperDraftPatch, usePaperDraftSave, useRollNumbers, useSectionsList,
} from '../../api/hooks';
import type { PaperDraftSaveError } from '../../api/types';
import { Badge, Button, ErrorPanel, PageHeader, PanelCard, Spinner } from '../../components/ui';
import UploadStep, { type UploadStepValue, type UploadSummary } from './components/UploadStep';
import QuestionsReview, { emptyQuestion, extractSaveError, type QuestionsReviewHandle, type ReviewSummary } from './components/QuestionsReview';
import SectionsStep, { audienceOptions } from './components/SectionsStep';
import AnswerSheetPreview from './components/AnswerSheetPreview';
import RollNumbersModal from './components/RollNumbersModal';
import { plural } from '../../lib/plural';

const STEPS = ['Exam source', 'Questions & key', 'Sections & students', 'Answer sheet'];
const STEP_SUB = [
  'Tell us the grade and subject, then add the blank paper so questions can be read for you.',
  'Check what was read from the paper. Set an answer key for every bubbled question.',
  'Pick who sits the exam. Roll numbers on the sheet match each scan to a student.',
  'This is what students fill in. Save the exam, then print the PDF and photocopy it for the class.',
];
const LAYOUT = 'A4 · 1 per page';

const AFTER_PRINTING = [
  { title: 'Students bubble answers', body: 'Dark pencil, one bubble per row.' },
  { title: 'Photograph the sheets', body: 'Flat, all four corner marks visible.' },
  { title: 'Upload and review', body: 'Only unclear marks need you.' },
];

interface StepInfo { label: string; hint: string; reachable: boolean }

/** Artboard `.steps`: four clickable cards, done / current / upcoming. */
function Stepper({ current, steps, onPick, pending }: { current: number; steps: StepInfo[]; onPick: (n: number) => void; pending: boolean }) {
  return (
    <nav aria-label="Steps" className="mb-[18px] flex flex-wrap gap-2.5">
      {steps.map((s, i) => {
        const n = i + 1;
        const done = n < current;
        const cur = n === current;
        return (
          <button
            key={s.label} type="button"
            aria-current={cur ? 'step' : undefined}
            disabled={!s.reachable || pending}
            onClick={() => onPick(n)}
            className={`flex min-w-0 flex-[1_1_200px] items-center gap-3 rounded-[16px] border bg-surface px-3.5 py-3 text-left text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent enabled:hover:border-line-hover disabled:cursor-not-allowed ${
              cur ? 'border-ink shadow-[inset_0_0_0_1px_rgb(var(--yd-ink))]' : 'border-line'
            }`}>
            <span
              className={`grid h-8 w-8 flex-none place-items-center rounded-full font-mono text-[13px] font-semibold ${
                cur ? 'bg-accent text-navy' : done ? 'bg-slate text-white' : 'bg-ground text-muted'
              }`}>
              {n}
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">{s.label}</span>
              <span className="block text-xs text-muted">{s.hint}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-hairline py-2.5 last:border-b-0">
      <dt className="text-muted">{label}</dt>
      <dd className="break-words text-right text-ink">{children}</dd>
    </div>
  );
}

function errorMessage(e: unknown): string | null {
  if (!e) return null;
  const data = (e as { response?: { data?: unknown } }).response?.data as Record<string, unknown> | undefined;
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string') return data.detail;
    const firstKey = Object.keys(data)[0];
    const firstVal = firstKey ? data[firstKey] : undefined;
    if (Array.isArray(firstVal) && typeof firstVal[0] === 'string') return firstVal[0];
    if (typeof firstVal === 'string') return firstVal;
  }
  return 'Something went wrong. Try again.';
}

/** "2 min ago" for the eyebrow; empty when the time is unknown. */
export function savedAgo(iso: string | undefined, now: number = Date.now()): string {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((now - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}


/** New paper exam (/exams/paper/new?draft=<id>). Four steps: the blank paper, the
 * questions and key, the sections that sit it, and the answer sheet. The draft is
 * created on step 1 and the exam saved on step 4 (spec §2 step 2, §7; plan B Task 4). */
export default function NewPaperExamPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const draftParam = searchParams.get('draft');
  const parsedDraftId = draftParam ? Number(draftParam) : null;
  const draftId = parsedDraftId != null && Number.isFinite(parsedDraftId) && parsedDraftId > 0 ? parsedDraftId : null;

  const draftQ = usePaperDraft(draftId);
  const createMut = usePaperDraftCreate();
  const patchMut = usePaperDraftPatch(draftId ?? 0);
  const saveMut = usePaperDraftSave(draftId ?? 0);
  const chaptersQ = useSchoolChapters(draftQ.data?.subject.id ?? null);
  const draft = draftQ.data;
  const gradeId = draft?.grade.id ?? null;
  const sectionsQ = useSectionsList(gradeId);
  const rollsQ = useRollNumbers(gradeId);

  // `null` = follow the flow (step 1 until the draft exists, then step 2); a number = the user picked a step.
  const [picked, setPicked] = useState<number | null>(null);
  // True from the click on Save exam (including the pre-save questions PATCH) until the save settles.
  const [saving, setSaving] = useState(false);
  const step = draftId == null ? 1 : (picked ?? 2);
  const [upload, setUpload] = useState<UploadSummary>({ name: '', gradeName: null, subjectName: null });
  const [review, setReview] = useState<ReviewSummary | null>(null);
  const [saveErr, setSaveErr] = useState<PaperDraftSaveError | null>(null);
  const [rollsOpen, setRollsOpen] = useState(false);
  const [chosen, setChosen] = useState<Set<number> | null>(null); // null = every section
  const examName = draft ? draft.name : upload.name;
  const gradeSubject = draft
    ? `${draft.grade.name} · ${draft.subject.name}`
    : [upload.gradeName, upload.subjectName].filter(Boolean).join(' · ');
  const reviewRef = useRef<QuestionsReviewHandle>(null);
  const canSaveDraft = draftId != null && draft?.status === 'ready';
  const questionsReady = !!review?.canContinue && draft?.status === 'ready';

  // Audience: the grade's sections (and the students with none), from the roll-number roster.
  const rosterRows = useMemo(() => rollsQ.data?.students ?? [], [rollsQ.data]);
  const options = useMemo(() => {
    const unsectioned = rosterRows.filter((r) => !r.section).length;
    return audienceOptions(sectionsQ.data?.sections ?? [], unsectioned);
  }, [sectionsQ.data, rosterRows]);
  const selected = useMemo(() => chosen ?? new Set(options.map((o) => o.id)), [chosen, options]);
  const picks = options.filter((o) => selected.has(o.id));
  const students = options.length > 0 ? picks.reduce((n, o) => n + o.students, 0) : (rollsQ.data?.count ?? 0);
  const sectionNames = new Set(picks.filter((o) => o.id !== 0).map((o) => o.letter));
  const missingRoll = rosterRows
    .filter((r) => r.roll_number == null && (options.length === 0 || (r.section ? sectionNames.has(r.section) : selected.has(0))))
    .map((r) => ({ name: r.name, section: r.section }));
  const secLabel = picks.length === 0
    ? 'none selected'
    : `${draft?.grade.name.replace(/^grade\s+/i, '') ?? ''} · ${picks.map((o) => o.letter).join(', ')}`;
  const audienceKnown = !!draft && !sectionsQ.isLoading && !rollsQ.isLoading;

  function toggleSection(id: number) {
    setChosen((cur) => {
      const next = new Set(cur ?? options.map((o) => o.id));
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleUpload(value: UploadStepValue) {
    try {
      const created = await createMut.mutateAsync(value);
      const next = new URLSearchParams(searchParams);
      next.set('draft', String(created.id));
      setSearchParams(next, { replace: true });
      setPicked(null);
    } catch {
      // surfaced via createMut.error below
    }
  }

  function handleManualEntry() {
    patchMut.mutate({ questions: [emptyQuestion()] });
  }

  /** Step 4: the same save call as before; the notice rides in router state for the list. */
  async function handleSave() {
    if (saving) return;
    setSaving(true);
    try {
      await saveExam();
    } finally {
      setSaving(false);
    }
  }

  async function saveExam() {
    setSaveErr(null);
    // Store the current questions again right before saving (the old Save did PATCH then save), so the
    // exam is always saved from what is on screen.
    if (reviewRef.current && !(await reviewRef.current.commit())) { setPicked(2); return; }
    try {
      const res = await saveMut.mutateAsync();
      navigate('/exams/paper', {
        state: { notice: 'Exam saved. Print the answer sheet next.', examId: res.exam_id, examName },
      });
    } catch (e) {
      const err = extractSaveError(e);
      setSaveErr(err);
      if (err.questions.length > 0) setPicked(2);
    }
  }

  const steps: StepInfo[] = [
    { label: STEPS[0], hint: gradeSubject || 'Grade, subject and paper', reachable: true },
    {
      label: STEPS[1], reachable: draftId != null,
      hint: review && draftId != null ? `${plural(review.total, 'question')} · ${review.bubbled} bubbled` : 'Read from the paper',
    },
    {
      label: STEPS[2], reachable: questionsReady,
      hint: audienceKnown ? `${plural(picks.length, 'section')} · ${plural(students, 'student')}` : 'Who sits the exam',
    },
    { label: STEPS[3], reachable: questionsReady, hint: LAYOUT },
  ];
  const savedWhen = draft ? savedAgo(draft.updated_at) : '';
  const backTo = (n: number) => () => setPicked(n);
  /** Any route past step 2 (a step card included) stores the questions first, and stops on step 2 if that fails. */
  async function pickStep(n: number) {
    if (saving) return;
    if (n <= 2 || !reviewRef.current) { setPicked(n); return; }
    if (await reviewRef.current.commit()) { setSaveErr(null); setPicked(n); } else setPicked(2);
  }

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        eyebrow={<><Link className="hover:text-ink" to="/exams/paper">Paper exams</Link> · New{savedWhen ? ` · draft saved ${savedWhen}` : ''}</>}
        title="New paper exam"
        subtitle="Set up the exam, print the answer sheets, then scan them back."
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate('/exams/paper')}>Cancel</Button>
            {canSaveDraft && (
              <Button
                variant="ghost"
                icon={<Save size={18} strokeWidth={1.8} aria-hidden />}
                loading={patchMut.isPending}
                onClick={() => { void reviewRef.current?.saveChanges(); }}>
                Save draft
              </Button>
            )}
          </>
        }
      />
      <Stepper pending={patchMut.isPending || saving} current={step} steps={steps} onPick={(n) => { void pickStep(n); }} />
      <div className="grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <section aria-labelledby="step-title" className="min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card">
          <div className="mb-[18px]">
            <p className="mb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Step {step} of {STEPS.length}</p>
            <h2 id="step-title" className="font-display text-[22px] font-bold tracking-[-0.01em] text-ink">{STEPS[step - 1]}</h2>
            <p className="mt-1 text-[13px] text-muted">{STEP_SUB[step - 1]}</p>
          </div>

          {step === 1 && draftId == null && (
            <UploadStep
              onSubmit={handleUpload}
              onSummary={setUpload}
              onCancel={() => navigate('/exams/paper')}
              submitting={createMut.isPending}
              error={createMut.isError ? errorMessage(createMut.error) : null}
            />
          )}

          {step === 1 && draftId != null && (
            <div>
              {draftQ.isLoading && <div className="flex justify-center py-10"><Spinner size="md" /></div>}
              {draft && (
                <div className="space-y-3">
                  <dl className="text-sm">
                    <SummaryRow label="Grade · subject">{gradeSubject}</SummaryRow>
                    <SummaryRow label="Exam name">{draft.name}</SummaryRow>
                    <SummaryRow label="Paper">
                      {draft.files.length === 0 ? '—' : draft.files.map((f) => (f.pages ? `${f.name} (${plural(f.pages, 'page')})` : f.name)).join(', ')}
                    </SummaryRow>
                  </dl>
                  <p className="text-[13px] text-muted">The questions were read from this paper. Change them in the next step.</p>
                </div>
              )}
              <div className="mt-[22px] flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-[18px]">
                <Button variant="ghost" onClick={() => navigate('/exams/paper')}>Cancel</Button>
                <Button onClick={() => setPicked(2)} icon={<ArrowRight size={18} strokeWidth={1.8} aria-hidden />} className="flex-row-reverse">
                  Continue
                </Button>
              </div>
            </div>
          )}

          {draftId != null && draftQ.isError && (
            <ErrorPanel title="Could not load the draft" onRetry={() => draftQ.refetch()} />
          )}

          {draftId != null && draft && (
            <div hidden={step !== 2}>
              <QuestionsReview
                ref={reviewRef}
                draft={draft}
                chapters={chaptersQ.data ?? []}
                onManualEntry={handleManualEntry}
                onSummary={setReview}
                manualPending={patchMut.isPending}
                patchQuestions={(questions) => patchMut.mutateAsync({ questions })}
                patchPending={patchMut.isPending}
                saveError={saveErr}
                onContinue={() => { setSaveErr(null); setPicked(3); }}
                onBack={backTo(1)}
              />
              {draft.status !== 'ready' && (
                <div className="mt-[22px] flex items-center border-t border-hairline pt-[18px]">
                  <Button variant="ghost" icon={<ArrowLeft size={18} strokeWidth={1.8} aria-hidden />} onClick={backTo(1)}>Back</Button>
                </div>
              )}
            </div>
          )}

          {step === 3 && draft && (
            <div>
              <SectionsStep
                gradeName={draft.grade.name}
                options={options}
                selected={selected}
                onToggle={toggleSection}
                missingRoll={missingRoll}
                onAssignRolls={() => setRollsOpen(true)}
                loading={sectionsQ.isLoading || rollsQ.isLoading}
              />
              <div className="mt-[22px] flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-[18px]">
                <Button variant="ghost" icon={<ArrowLeft size={18} strokeWidth={1.8} aria-hidden />} onClick={backTo(2)}>Back</Button>
                <Button onClick={() => { void pickStep(4); }} icon={<ArrowRight size={18} strokeWidth={1.8} aria-hidden />} className="flex-row-reverse">
                  Preview answer sheet
                </Button>
              </div>
            </div>
          )}

          {step === 4 && draft && (
            <div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
                <Badge tone="neutral">{LAYOUT}</Badge>
                <span className="flex flex-wrap gap-3.5 text-xs text-muted">
                  <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-[2px] bg-navy" />Corner marks</span>
                  <span className="inline-flex items-center gap-1.5"><i className="box-border inline-block h-2.5 w-2.5 rounded-full border-[1.5px] border-muted" />Bubbles</span>
                  <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-full bg-accent" />Teacher-graded</span>
                </span>
              </div>
              <div className="rounded-[16px] bg-track p-[22px]">
                <AnswerSheetPreview
                  examName={draft.name}
                  gradeName={draft.grade.name}
                  nBubbled={review?.bubbled ?? 0}
                  nTeacher={review?.teacher ?? 0}
                  teacherNumbers={review?.teacherNumbers ?? []}
                  bubbledNumbers={review?.bubbledNumbers}
                />
              </div>
              <p className="mt-2.5 text-center text-xs text-muted">A4, one sheet per page · blank, for photocopying</p>
              {saveErr && saveErr.questions.length === 0 && <p role="alert" className="mt-3 text-sm text-danger">{saveErr.detail}</p>}
              <div className="mt-[22px] flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-[18px]">
                <Button variant="ghost" icon={<ArrowLeft size={18} strokeWidth={1.8} aria-hidden />} onClick={backTo(3)} disabled={saving}>Back</Button>
                <Button onClick={handleSave} loading={saving || saveMut.isPending} icon={<Check size={18} strokeWidth={1.8} aria-hidden />}>
                  Save exam
                </Button>
              </div>
            </div>
          )}
        </section>

        <aside aria-label="Summary" className="flex min-w-0 flex-col gap-[18px]">
          <PanelCard title="Summary" actions={<Badge tone="neutral">Draft</Badge>}>
            <dl className="text-sm">
              <SummaryRow label="Exam"><span className="font-semibold">{examName || '—'}</span></SummaryRow>
              <SummaryRow label="Grade · subject">{gradeSubject || '—'}</SummaryRow>
              {draftId != null && review ? (
                <>
                  <SummaryRow label="Questions">
                    <span className="font-mono font-medium">{review.total}</span>{' '}
                    <span className="text-xs text-muted">· {review.bubbled} bubbled, {review.teacher} written</span>
                  </SummaryRow>
                  <SummaryRow label="Answer key">
                    <Badge tone={review.keyOk ? 'success' : 'warning'}>{review.keyText}</Badge>
                  </SummaryRow>
                  {audienceKnown && (
                    <>
                      <SummaryRow label="Students">
                        <span className="font-mono font-medium">{students}</span>{' '}
                        <span className="text-xs text-muted">· {secLabel}</span>
                      </SummaryRow>
                      <SummaryRow label="Sheets to print"><span className="font-mono font-semibold">{students}</span></SummaryRow>
                    </>
                  )}
                  <SummaryRow label="Layout">{LAYOUT}</SummaryRow>
                </>
              ) : (
                <SummaryRow label="Questions"><span className="text-muted">Read from the paper next</span></SummaryRow>
              )}
            </dl>
          </PanelCard>

          <section className="flex min-w-0 flex-col gap-4 rounded-[22px] bg-navy p-6 text-white">
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">After printing</div>
            <div className="flex flex-col gap-3">
              {AFTER_PRINTING.map((a, i) => (
                <div key={a.title} className="flex gap-3">
                  <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[12px] bg-mist text-xs font-semibold text-ink">{i + 1}</span>
                  <span>
                    <span className="block font-semibold">{a.title}</span>
                    <span className="text-[13px] text-on-navy-muted">{a.body}</span>
                  </span>
                </div>
              ))}
            </div>
            <Button variant="ghost" className="w-full flex-row-reverse" onClick={() => navigate('/exams/paper')} icon={<ArrowRight size={18} strokeWidth={1.8} aria-hidden />}>
              Go to paper exams
            </Button>
          </section>
        </aside>
      </div>

      {draft && (
        <RollNumbersModal gradeId={draft.grade.id} gradeName={draft.grade.name} isOpen={rollsOpen} onClose={() => setRollsOpen(false)} />
      )}
    </div>
  );
}
